import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Bell, CheckCircle, Warning, ShieldWarning, Siren,
  Clock, Funnel, Trash, CheckSquare, Eye, X,
  CalendarBlank, Wrench, CurrencyDollar, DeviceMobile, Lightning
} from '@phosphor-icons/react';

const SEVERITY_CONFIG = {
  critical: { color: '#EF4444', bg: 'bg-red-500/10', border: 'border-red-500/20', label: 'Critical' },
  high: { color: '#FF6B35', bg: 'bg-orange-500/10', border: 'border-orange-500/20', label: 'High' },
  medium: { color: '#F59E0B', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', label: 'Medium' },
  low: { color: '#22C55E', bg: 'bg-green-500/10', border: 'border-green-500/20', label: 'Normal' },
};

const TYPE_ICONS = {
  security_alert: ShieldWarning,
  panic: Siren,
  reminder: Clock,
  morning_reminder: Bell,
  evening_reminder: Bell,
  screen_time: DeviceMobile,
  warranty_alert: Wrench,
  low_balance: CurrencyDollar,
  birthday: CalendarBlank,
  medicine_refill: Lightning,
  salary_reminder: CurrencyDollar,
  device_alert: DeviceMobile,
};

export default function AlertCenterPage() {
  const { api } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ priority: '', unread: false });
  const [stats, setStats] = useState({ total: 0, unread: 0, critical: 0 });
  const [selectedAlert, setSelectedAlert] = useState(null);

  const fetchData = async () => {
    try {
      const res = await api.get(`/notifications${filter.unread ? '?unread_only=true' : ''}`);
      let data = res.data;
      if (filter.priority) {
        data = data.filter(n => n.priority === filter.priority);
      }
      setNotifications(data);
      setStats({
        total: res.data.length,
        unread: res.data.filter(n => !n.read).length,
        critical: res.data.filter(n => n.priority === 'critical').length
      });
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [api, filter]);

  const markRead = async (id) => {
    try { await api.put(`/notifications/${id}/read`); fetchData(); } catch (e) { console.error(e); }
  };

  const markAllRead = async () => {
    try { await api.put('/notifications/read-all'); fetchData(); } catch (e) { console.error(e); }
  };

  const getPriorityConfig = (priority) => SEVERITY_CONFIG[priority] || SEVERITY_CONFIG.low;

  const getTypeIcon = (notif) => {
    const metaType = notif.metadata?.type || notif.type || '';
    return TYPE_ICONS[metaType] || Bell;
  };

  const timeAgo = (ts) => {
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="alert-center-page" className="space-y-6 relative">
      <div className="glow-orb-orange -top-20 -left-20 z-0" />

      {/* Header */}
      <div className="relative z-10 animate-fade-up flex items-start justify-between">
        <div>
          <p className="text-neutral-500 text-sm font-medium">Central Hub</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Alert Center</h1>
          <p className="text-neutral-500 text-sm mt-1">All notifications from every module in one place</p>
        </div>
        {stats.unread > 0 && (
          <button onClick={markAllRead} data-testid="mark-all-read-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-xs text-neutral-300 hover:bg-white/10 transition-colors">
            <CheckSquare size={14} /> Mark All Read
          </button>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4 relative z-10 animate-fade-up" style={{ animationDelay: '100ms' }}>
        <div className="glass-card p-4" data-testid="stat-total-alerts">
          <p className="overline mb-1">Total Alerts</p>
          <p className="text-2xl font-heading font-bold text-white">{stats.total}</p>
        </div>
        <div className="glass-card p-4" data-testid="stat-unread">
          <p className="overline mb-1">Unread</p>
          <p className="text-2xl font-heading font-bold text-[#FF6B35]">{stats.unread}</p>
        </div>
        <div className="glass-card p-4" data-testid="stat-critical">
          <p className="overline mb-1">Critical</p>
          <p className="text-2xl font-heading font-bold text-red-400">{stats.critical}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 relative z-10 animate-fade-up" style={{ animationDelay: '150ms' }}>
        <Funnel size={14} className="text-neutral-500" />
        <button onClick={() => setFilter(f => ({ ...f, priority: '' }))} data-testid="filter-all"
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!filter.priority ? 'bg-white/10 text-white' : 'bg-white/5 text-neutral-500 hover:text-white'}`}>All</button>
        {['critical', 'high', 'medium'].map(p => (
          <button key={p} onClick={() => setFilter(f => ({ ...f, priority: f.priority === p ? '' : p }))} data-testid={`filter-${p}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter.priority === p ? `${getPriorityConfig(p).bg} ${getPriorityConfig(p).border} border` : 'bg-white/5 text-neutral-500 hover:text-white'}`}
            style={filter.priority === p ? { color: getPriorityConfig(p).color } : {}}>
            {getPriorityConfig(p).label}
          </button>
        ))}
        <button onClick={() => setFilter(f => ({ ...f, unread: !f.unread }))} data-testid="filter-unread"
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter.unread ? 'bg-[#FF6B35]/10 text-[#FF6B35] border border-[#FF6B35]/20' : 'bg-white/5 text-neutral-500 hover:text-white'}`}>
          Unread Only
        </button>
      </div>

      {/* Alert List */}
      {notifications.length === 0 ? (
        <div className="glass-card p-12 text-center relative z-10 animate-fade-up">
          <CheckCircle size={48} weight="duotone" className="text-green-400 mx-auto mb-3" />
          <p className="text-neutral-400 text-sm">All clear! No alerts matching your filter.</p>
        </div>
      ) : (
        <div className="space-y-2 relative z-10">
          {notifications.map((n, idx) => {
            const config = getPriorityConfig(n.priority);
            const Icon = getTypeIcon(n);
            return (
              <div key={n.notification_id || idx}
                data-testid={`alert-${n.notification_id}`}
                onClick={() => { setSelectedAlert(n); if (!n.read) markRead(n.notification_id); }}
                className={`glass-card p-4 flex items-start gap-4 cursor-pointer hover:bg-white/[0.03] transition-colors animate-fade-up ${!n.read ? 'border-l-2' : 'opacity-70'}`}
                style={{ animationDelay: `${200 + idx * 30}ms`, borderLeftColor: !n.read ? config.color : 'transparent' }}>
                {/* Icon */}
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${config.bg}`}>
                  <Icon size={18} weight="duotone" style={{ color: config.color }} />
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className={`font-semibold text-sm ${!n.read ? 'text-white' : 'text-neutral-400'}`}>{n.title}</h3>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[#FF6B35] flex-shrink-0" />}
                  </div>
                  <p className="text-neutral-500 text-xs line-clamp-2">{n.message}</p>
                </div>
                {/* Time + Priority */}
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-neutral-600 text-[10px]">{timeAgo(n.created_at)}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${config.bg} font-semibold`} style={{ color: config.color }}>
                    {config.label}
                  </span>
                  {n.channel && n.channel !== 'in_app' && (
                    <span className="text-[9px] text-neutral-600">{n.channel}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedAlert(null)}>
          <div className="glass-card w-full max-w-lg p-6 animate-fade-up" data-testid="alert-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getPriorityConfig(selectedAlert.priority).bg}`}>
                  {React.createElement(getTypeIcon(selectedAlert), { size: 20, weight: 'duotone', style: { color: getPriorityConfig(selectedAlert.priority).color } })}
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-white">{selectedAlert.title}</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${getPriorityConfig(selectedAlert.priority).bg} font-semibold`}
                    style={{ color: getPriorityConfig(selectedAlert.priority).color }}>
                    {getPriorityConfig(selectedAlert.priority).label}
                  </span>
                </div>
              </div>
              <button onClick={() => setSelectedAlert(null)} className="text-neutral-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5 mb-4">
              <p className="text-neutral-300 text-sm whitespace-pre-wrap">{selectedAlert.message}</p>
            </div>
            <div className="flex items-center justify-between text-xs text-neutral-500">
              <span>{new Date(selectedAlert.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>
              {selectedAlert.metadata?.type && (
                <span className="px-2 py-0.5 rounded-full bg-white/5">{selectedAlert.metadata.type.replace(/_/g, ' ')}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
